import round from 'lodash/round';

import { SRCSET_WIDTHS, buildImgixUrl, pickFallbackWidth, snapWidth } from 'Helpers/Image/image-urls';

// FluidImg's fallback ceiling when `sizes` has no px widths
const MAX_FALLBACK_WIDTH = 400;

// As in FluidImg: a ladder width within 15% of a `sizes` width is left out
const nearAny = (ladderWidth, widths) =>
  widths.some(width => (width / ladderWidth) > 0.85 && (width / ladderWidth) < 1.15);

/**
 * A `sources` builder for SkeletonImg that matches FluidImg with a `sizesOverride`: the px widths in `sizes` and
 * their doubles (snapped, as FluidImg does), plus the ladder widths that aren't near them. FluidImg also adds the
 * widths of any `vw` entries for the current window, which a build-time skeleton can't know, so only px entries
 * count here.
 *
 * @param {string} sizes        - The real component's `sizesOverride`.
 * @param {number} aspectRatio  - Crop ratio, as FluidImg's `aspectRatio` (or 16 / 9 for `ratio16by9`); 0 for none.
 * @returns {function(string): {srcSet: string, src: string}}
 */
export default function fluidSizesSources(sizes, aspectRatio = 0) {
  const pxWidths = sizes.split(',')
    .map(part => part.trim().match(/(\d+)px$/))
    .filter(Boolean)
    .map(match => Number(match[1]));
  const doubled = [...pxWidths, ...pxWidths.map(width => width * 2)];
  const sizeWidths = [...new Set(doubled.map(snapWidth))];
  const ladderWidths = SRCSET_WIDTHS.filter(width => !nearAny(width, sizeWidths));
  const widths = [...new Set([...sizeWidths, ...ladderWidths])].sort((a, b) => b - a);
  const maxFallback = pxWidths.length ? Math.max(...pxWidths) : MAX_FALLBACK_WIDTH;

  return (url) => {
    const toUrl = width => buildImgixUrl(url, {
      width,
      ...(aspectRatio ? { fit: 'crop', height: round(width / aspectRatio) } : { fit: 'max' }),
    });
    return {
      srcSet: widths.map(width => `${toUrl(width)} ${width}w`).join(', '),
      src: toUrl(pickFallbackWidth(widths, maxFallback)),
    };
  };
}
